import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Trash2, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminFormConfig = () => {
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFormConfig();
  }, []);

  const fetchFormConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/form-config`);
      setFields(response.data.fields || []);
    } catch (error) {
      toast.error('獲取表單配置失敗');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/form-config`, { fields });
      toast.success('表單配置已保存');
    } catch (error) {
      toast.error('保存失敗');
    }
  };

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      label: '',
      field_type: 'text',
      required: false,
      options: [],
      placeholder: '',
      order: fields.length + 1
    };
    setFields([...fields, newField]);
  };

  const removeField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const updateField = (index, key, value) => {
    const updated = [...fields];
    updated[index][key] = value;
    setFields(updated);
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/users')}
              className="rounded-xl text-atelier-ink hover:text-white hover:bg-white/5"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              返回
            </Button>
            <h1 className="font-display text-3xl font-semibold text-atelier-ink tracking-tight">
              註冊表單配置
            </h1>
          </div>
          <Button
            onClick={saveConfig}
            className="rounded-full btn-neon font-semibold"
          >
            保存配置
          </Button>
        </div>

        {/* Form Fields */}
        <div className="bg-white rounded-2xl shadow-xl p-6 border border-neutral-200 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-neutral-800">表單字段</h2>
            <Button
              onClick={addField}
              className="rounded-xl bg-primary-600 hover:bg-primary-700 text-white"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              添加字段
            </Button>
          </div>

          <div className="space-y-4">
            {fields.map((field, index) => (
              <div key={field.id} className="bg-neutral-50 rounded-xl p-4 border border-neutral-200">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 pt-3 cursor-move">
                    <GripVertical className="w-5 h-5 text-neutral-400" />
                  </div>
                  
                  <div className="flex-1 grid grid-cols-2 gap-4">
                    {/* Label */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">字段名稱</label>
                      <Input
                        value={field.label}
                        onChange={(e) => updateField(index, 'label', e.target.value)}
                        placeholder="例如：帳號定位"
                        className="rounded-lg"
                      />
                    </div>

                    {/* Field Type */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">字段類型</label>
                      <Select
                        value={field.field_type}
                        onValueChange={(value) => updateField(index, 'field_type', value)}
                      >
                        <SelectTrigger className="rounded-lg">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">單行文本</SelectItem>
                          <SelectItem value="textarea">多行文本</SelectItem>
                          <SelectItem value="select">下拉選單</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Placeholder */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">提示文字</label>
                      <Input
                        value={field.placeholder}
                        onChange={(e) => updateField(index, 'placeholder', e.target.value)}
                        placeholder="例如：請輸入您的帳號定位"
                        className="rounded-lg"
                      />
                    </div>

                    {/* Required */}
                    <div className="flex items-center pt-8">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(index, 'required', e.target.checked)}
                        className="w-4 h-4 text-primary-600 rounded"
                      />
                      <label className="ml-2 text-sm text-neutral-700">必填字段</label>
                    </div>

                    {/* Options (for select type) */}
                    {field.field_type === 'select' && (
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-neutral-700 mb-2">選項（用逗號分隔）</label>
                        <Input
                          value={Array.isArray(field.options) ? field.options.join(', ') : ''}
                          onChange={(e) => updateField(index, 'options', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                          placeholder="例如：專業, 輕鬆, 幽默"
                          className="rounded-lg"
                        />
                      </div>
                    )}
                  </div>

                  {/* Delete Button */}
                  <Button
                    onClick={() => removeField(index)}
                    variant="outline"
                    size="sm"
                    className="rounded-lg border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}

            {fields.length === 0 && (
              <div className="text-center py-12 text-neutral-500">
                尚未添加任何字段，點擊「添加字段」開始配置
              </div>
            )}
          </div>
        </div>

        {/* Preview */}
        <div className="bg-white rounded-2xl shadow-xl p-6 border border-neutral-200">
          <h2 className="text-lg font-semibold text-neutral-800 mb-4">預覽</h2>
          <div className="space-y-4">
            {fields.map(field => (
              <div key={field.id}>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </label>
                {field.field_type === 'text' && (
                  <Input placeholder={field.placeholder} className="rounded-lg" disabled />
                )}
                {field.field_type === 'textarea' && (
                  <textarea 
                    placeholder={field.placeholder} 
                    className="w-full rounded-lg border-neutral-300 p-3 text-sm bg-white" 
                    rows={3}
                    disabled
                  />
                )}
                {field.field_type === 'select' && (
                  <Select disabled>
                    <SelectTrigger className="rounded-lg">
                      <SelectValue placeholder="請選擇..." />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options.map((opt, i) => (
                        <SelectItem key={i} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ))}
            
            {fields.length === 0 && (
              <div className="text-center py-8 text-neutral-400">
                添加字段後即可預覽
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminFormConfig;
